import React, { Fragment, useEffect } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import ForcedAutopayPaymentDetails from './ForcedAutopayPaymentDetails';

import { WarningIcon } from 'components/Shared/Icons/Icons';
import Recurring from 'components/Shared/Icons/Recurring';
import Separator from 'components/Shared/Separator/Separator';
import WavefrontReporter from 'reporting/wavefront/WavefrontReporter';
import { paymentMethodDisplaySelector } from 'store/payment/selectors';
import {
  autoPayIntervalSelector,
  emailFromSaleSelector,
  nextPaymentDateSelector,
  saleSelectors,
} from 'store/sale/selectors';
import { useSelector } from 'store/utils';
import { colors, breakpoints } from 'styles/cp';

const wavefrontReporter = WavefrontReporter.getInstance();

interface ForcedAutopayScheduledContentProps {
  balance: number;
  currency: string;
  companyName: string;
  isUserSignedIn: boolean;
  isSignedInUserTheScheduler?: boolean;
  signInCallback: () => void;
  openModal: () => void;
  isPaymentScheduleSuccessful?: boolean;
}

const ForcedAutopayScheduledContent: React.FC<ForcedAutopayScheduledContentProps> = ({
  balance,
  currency,
  companyName,
  isUserSignedIn,
  isSignedInUserTheScheduler = false,
  signInCallback,
  openModal,
  isPaymentScheduleSuccessful = false,
}) => {
  const emailFromSale = useSelector(emailFromSaleSelector);
  const paymentMethod = useSelector(paymentMethodDisplaySelector);

  const nextPaymentDate = useSelector(nextPaymentDateSelector);
  const autoPayInterval = useSelector(autoPayIntervalSelector);
  const invoiceAmount = useSelector((state) => saleSelectors.amountSelector(state.sale));

  const displayAmount = balance === 0 ? invoiceAmount : balance;

  useEffect(() => {
    wavefrontReporter.trackForcedAutopayScheduledViewed({
      isPostSchedule: isPaymentScheduleSuccessful,
    });
  }, [isPaymentScheduleSuccessful]);

  return (
    <Fragment>
      <div className="forced-autopay-content">
        <div className="invoice-amount-with-interval">
          <FormattedNumber value={displayAmount} style="currency" currency={currency} />
          <span className="interval"> / {autoPayInterval}</span>
        </div>
        <Separator height={24} />

        <div className="payment-details">
          {isUserSignedIn ? (
            <ForcedAutopayPaymentDetails
              balance={balance}
              currency={currency}
              companyName={companyName}
              emailFromSale={emailFromSale}
              paymentMethod={paymentMethod}
            />
          ) : (
            <div className="merchant-name">{companyName}</div>
          )}
        </div>
        <Separator height={20} />

        {/* Next autopay section */}
        <div className="next-autopay-section">
          <div className="next-autopay-header">
            <FormattedMessage id="AUTOPAY_SUCCESS_SCREEN_HEADER" defaultMessage="Next autopay" />
          </div>
          <Separator height={16} />
          <div className="next-autopay-info">
            <Recurring width={20} height={20} color={colors.green} />
            <div className="next-autopay-text">
              {nextPaymentDate && (
                <div className="next-payment-date">
                  <FormattedMessage
                    id="AUTOPAY_NEXT_PAYMENT_DATE"
                    defaultMessage="Next payment date: {date}"
                    values={{ date: nextPaymentDate }}
                  />
                </div>
              )}
              <div className="continues-text">
                <FormattedMessage
                  id="AUTOPAY_CONTINUES_INTERVAL_EMAIL"
                  defaultMessage="Continues {interval}. Check your email for additional details."
                  values={{ interval: autoPayInterval }}
                />
              </div>
            </div>
          </div>
        </div>
        <Separator height={32} />

        <div className="action-section">
          {!isUserSignedIn && (
            <div className="to-manage">
              <FormattedMessage
                id="AUTOPAY_TO_MANAGE_AUTOPAY"
                defaultMessage="To manage autopay, "
              />
              <span className="sign-in-link" onClick={signInCallback}>
                <FormattedMessage id="SIGN_IN" defaultMessage="sign in" />
              </span>
            </div>
          )}
          {isUserSignedIn && isSignedInUserTheScheduler && (
            <div className="action-buttons">
              <div className="cancel-autopay-link" onClick={openModal}>
                <FormattedMessage id="CANCEL_AUTOPAY" defaultMessage="Cancel autopay" />
              </div>
            </div>
          )}
          {isUserSignedIn && !isSignedInUserTheScheduler && (
            <div className="scheduled-by-someone-else">
              <span className="warning">
                <WarningIcon width={20} height={20} color={colors.gray} />
              </span>
              <FormattedMessage
                id="SUBSCRIPTION_BY_SOMEONE_ELSE"
                defaultMessage="Another recipient of this invoice has set up autopay."
              />
            </div>
          )}
        </div>
        <Separator height={20} />
      </div>

      <style jsx>{`
        .forced-autopay-content {
          width: 100%;
          display: flex;
          align-items: center;
          flex-direction: column;

          @media screen and (max-width: ${breakpoints.sm}) {
            padding: 0 20px;
          }

          .invoice-amount-with-interval {
            font-family: AvenirNextforINTUIT-Demi;
            font-size: 24px;
            color: ${colors.gray};
          }

          .payment-details {
            width: 100%;
            max-width: 432px;

            :global(li) {
              margin-bottom: 8px;
            }

            .merchant-name {
              font-family: AvenirNextforINTUIT-Demi;
              font-size: 16px;
              color: ${colors.darkGray};
              text-align: center;
              margin-bottom: 8px;
            }
          }

          .next-autopay-section {
            width: 100%;
            max-width: 432px;
            border-top: 1px solid ${colors.gray07};
            border-bottom: 1px solid ${colors.gray07};
            padding: 20px 0;

            .next-autopay-header {
              font-family: AvenirNextforINTUIT-Demi;
              font-size: 14px;
              color: ${colors.darkGray};
            }

            .next-autopay-info {
              display: flex;
              align-items: center;
              gap: 12px;

              .next-autopay-text {
                font-family: AvenirNextforINTUIT-Regular;
                font-size: 14px;
                line-height: 1.4;

                .next-payment-date {
                  color: ${colors.darkGray};
                }

                .continues-text {
                  color: ${colors.gray};
                }
              }
            }
          }

          .action-section {
            width: 100%;
            max-width: 432px;

            .to-manage {
              text-align: center;
              font-family: AvenirNextforINTUIT-Demi;
              font-size: 14px;
              color: ${colors.darkGray};

              .sign-in-link {
                color: ${colors.blue};
                cursor: pointer;
                text-decoration: none;
              }
            }

            .action-buttons {
              display: flex;
              flex-direction: column;
              gap: 10px;
              align-items: center;

              .cancel-autopay-link {
                cursor: pointer;
                color: ${colors.blue};
                font-family: AvenirNextforINTUIT-Medium;
                font-size: 14px;
                text-decoration: none;
              }
            }

            .scheduled-by-someone-else {
              color: ${colors.gray02};
              font-family: AvenirNextforINTUIT-Regular;
              font-size: 14px;
              display: flex;
              justify-items: center;

              .warning {
                padding: 0 10px 0 0;
              }
            }
          }
        }
      `}</style>
    </Fragment>
  );
};

export default ForcedAutopayScheduledContent;
